// PHASE_4 / B1-frontend — task UX capability derivation (ScopeGate convenience).
//
// PURE helper: derives the task capability strings the <ScopeGate> component
// hides actions behind from the caller's RESOLVED effective permissions
// (useEffectivePermissions capability set) — never from the navigation hat
// (SEM-5131: the hat is gone; d0d143bf converted the four consumed helpers,
// this one follows the identical leaveCapabilities pattern). CONVENIENCE
// ONLY — the server re-checks every request (ADR-017; the frontend is never
// a trust boundary, ADR-008). The lifecycle actions (complete/dismiss/snooze)
// are ASSIGNEE-only server-side regardless: the route 403s a non-assignee
// even if the UI showed the button.
//
// Capability vocabulary (mirrors the route's perm_tasks_* seam, spec 16 — read
// = employee_self / update = employee_self / create = manager_subtree):
//   * tasks.read        — read own inbox (every employee); a broader caller's
//                         read widens to their tier SERVER-side.
//   * tasks.act_own     — complete / dismiss / snooze your OWN task (every
//                         employee — the server asserts assignee in-handler).
//   * tasks.create      — manually create a task assigned to someone
//                         (permission-derived; manager_subtree+ server-side).

export const TASK_CAP_READ = "tasks.read";
export const TASK_CAP_ACT_OWN = "tasks.act_own";
export const TASK_CAP_CREATE = "tasks.create";

/**
 * The catalog cell the manual-create affordance derives from. The matrix has
 * no "tasks" module — the menu/legacy vocabulary canonicalises tasks→requests
 * (use-effective-permissions MODULE_SYNONYM), so the resolved cell is
 * requests.create. NOTE the cell alone is a weak discriminator (an employee
 * holds requests.create for their OWN requests); the server's manager_subtree
 * tier remains the real boundary, this only hides a doomed button.
 */
export const TASK_CREATE_CELL = "requests.create";

/**
 * The task capabilities the caller's resolved permission set exposes in the
 * UI. Reading the inbox + acting on OWN tasks is UNCONDITIONAL (inherent to
 * being an employee — deriving them from the default-closed matrix would hide
 * every employee's own inbox); only manual creation is permission-derived.
 * Returned as a readonly Set for O(1) <ScopeGate> membership tests.
 *
 * @param allowed the caller's resolved `<module>.<action>` capability set
 *   (useEffectivePermissions().capabilities) — empty while loading/on error,
 *   so the create affordance is default-closed until permissions resolve.
 */
export function taskCapabilities(allowed: ReadonlySet<string>): ReadonlySet<string> {
  const caps = new Set<string>([TASK_CAP_READ, TASK_CAP_ACT_OWN]);
  if (allowed.has(TASK_CREATE_CELL)) {
    caps.add(TASK_CAP_CREATE);
  }
  return caps;
}
